
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { getOrderStatus, getOrderType, getOrderPaymentPreference, getDisplayOrderNumber } from '@/utils/getters';
import { decodeItems } from '@/utils/decoding';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import OrderInfo from '@/components/order-management/OrderInfo.vue';
import OrderDetails from '@/components/order-management/OrderDetails.vue';
import RequestDepositModal from '@/components/order-management/RequestDepositModal.vue';
import CancelSchoolOrderModal from '@/components/order-management/CancelSchoolOrderModal.vue';
import DeclineOrderModal from '@/components/order-management/DeclineOrderModal.vue';
import RefundRequestModal from '@/components/order-management/RefundRequestModal.vue';
import OrderModalActions from '@/components/order-management/OrderModalActions.vue';
import GenericCateringWarningOrderModal from '@/components/order-management/GenericCateringWarningOrderModal.vue';
import ModifySchoolDeliveryModal from '@/components/order-management/ModifySchoolDeliveryModal.vue';
import OrderModificationRequestModal from './OrderModificationRequestModal.vue';

const namespace: string = 'orders';

type WarningModalProps = {
	show: boolean;
	title: string;
	text: string;
	confirmButtonText: string | undefined;
	cancelButtonText: string;
	onConfirm: Function;
}

@Component<OrderModal>({
	components: {
		StandardModal,
		OrderInfo,
		OrderDetails,
		RequestDepositModal,
		CancelSchoolOrderModal,
		DeclineOrderModal,
		RefundRequestModal,
		WarningModal,
		OrderModalActions,
		GenericCateringWarningOrderModal,
		ModifySchoolDeliveryModal,
		TextButton,
		OrderModificationRequestModal
	}
})
export default class OrderModal extends Vue {
	@Action('updateOrder', { namespace }) private updateOrder!: (payload: UpdateOrderPayload) => Promise<void>;
	@Action('sendDepositRequest', { namespace }) private sendDepositRequest!: (payload: RequestDepositPayload) => Promise<void>;
	@Action('refundOrder', { namespace }) private refundOrder!: (payload: RefundOrderPayload) => Promise<void>;
	@Action('getOrderCosts', { namespace }) private getOrderCosts!: (order: Order) => Promise<{ orderItems: OrderItem[], costs: Costs }>;
	@Action('bulkActions', { namespace }) private bulkActions!: (payload: BulkActionPayload) => BulkActionResponse;
	@Action('fetchInquiryFormRequestById', { namespace: 'inquiryForm' }) private fetchInquiryFormRequestById!: (requestId: number) => Promise<void>;
	@Getter('isMarketplaceEnabled', { namespace: 'auth' }) private isMarketplaceEnabled!: boolean;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getStaffEmail', { namespace: 'auth' }) private staffEmail!: string | null;
	@Prop({ type: Object, required: true }) private order!: Order;

	private isSuitesCateringEditing: boolean = false;
	private disableSave: boolean = true;
	private loadingItemIndex: number = -1;
	private mutableOrder: Order = cloneDeep(this.order);
	private loading: { [key: string]: boolean } = {
		accept: false,
		requestDeposit: false,
		decline: false,
		complete: false,
		refund: false,
		refundRequest: false,
		save: false,
		modifySchoolDelivery: false,
		cancel: false
	};
	private orderStatusMapping: { [key: string]: string } = {
		accept: 'in-progress',
		decline: 'cancelled',
		complete: 'completed'
	};
	private showModal: { [key: string]: boolean } = {
		requestDeposit: false,
		declineOrder: false,
		cancelSchoolOrder: false,
		genericCateringComplete: false,
		chargeOrInvoice: false,
		refundRequest: false,
		modifySchoolDelivery: false,
		orderModificationRequest: false
	};
	private warningModalProps: WarningModalProps = {
		show: false,
		title: '',
		text: '',
		confirmButtonText: '',
		cancelButtonText: '',
		onConfirm: () => {}
	};

	private get modalTitle(): string {
		const event = this.mutableOrder.data.checkout.cateringEvent;
		if (event) {
			return `${Vue.filter('localize')(event, 'name')} <span class="label-sm" style="font-size: 1rem">(#${this.orderNumber})</span>`;
		}
		return this.$t('orders.management.parent_card.order_card.order_number', { orderNumber: this.orderNumber });
	}

	private get email(): string | undefined {
		return this.order.data.checkout?.contact?.email;
	}

	private get orderNumber(): string {
		return getDisplayOrderNumber(this.mutableOrder.id, this.mutableOrder.restaurant_id);
	}

	private get parentOrderType(): ParentOrderType {
		return getOrderType(this.mutableOrder);
	}

	private get orderStatus(): string {
		// For K12 locations, use specific translation for cancelled status
		if (this.isK12Location && this.mutableOrder.status === 'cancelled') {
			return this.$t('orders.management.parent_card.order_card.cancelled_k12');
		}
		return getOrderStatus(this.mutableOrder.status);
	}

	private get orderPaymentPreference(): string | undefined {
		return getOrderPaymentPreference(this.mutableOrder, this.restaurant.extra_payment_methods);
	}

	private get items(): OrderItem[] {
		return this.mutableOrder.data.items;
	}

	private get genericCateringCompleteModalDescription(): string | undefined {
		if (this.mutableOrder.type === 'generic-catering') {
			const i18nPathDepositModal: string = 'orders.management.request_deposit_modal.warning';
			if (this.mutableOrder.data.checkout?.invoice) {
				return this.$t(`${i18nPathDepositModal}.invoice_description`);
			}
			else if (this.mutableOrder.data.checkout?.costCenter) {
				return this.$t(`${i18nPathDepositModal}.cost_center_and_purchase_order_description`);
			}
			else if (this.mutableOrder.data.checkout?.purchaseOrder) {
				return this.$t(`${i18nPathDepositModal}.cost_center_and_purchase_order_description`);
			}
			return this.$t(`${i18nPathDepositModal}.credit_card_description`);
		}
	}

	private get isK12AndMisconfigured(): boolean {
		const { menu_configuration, menu_manager_config } = this.restaurant;
		const timezone = menu_configuration?.zone || 'America/Toronto';
		return this.isK12Location
			&& !!menu_manager_config
			&& (!menu_manager_config.end_school_year_date
				|| DateTime.now().setZone(timezone).endOf('day') > DateTime.fromISO(menu_manager_config.end_school_year_date, { zone: timezone }).endOf('day'));
	}

	private get quoteInfoString(): string {
		const quoteInfo = this.mutableOrder.data.checkout.quoteInfo;
		if (!quoteInfo?.quoteExpiresAt || this.order.status !== 'pending') {
			return '';
		}
		const formattedExpiry = DateTime.fromISO(quoteInfo.quoteExpiresAt).toFormat('yyyy/LL/dd');

		if (quoteInfo.quoteBehavior === 'requireDeposit') {
			return this.$t('orders.management.order_modal.quote_deposit_info', { amount: quoteInfo.amount, date: formattedExpiry });
		}
		if (quoteInfo.quoteBehavior === 'requireApproval') {
			return this.$t('orders.management.order_modal.quote_approval_info', { date: formattedExpiry });
		}
		return '';
	}

	private get inquiryRequestString(): string {
		if (!this.mutableOrder.inquiry_form_request) {
			return '';
		}
		const formattedDate = DateTime.fromISO(this.mutableOrder.inquiry_form_request.created_at).setLocale(this.$i18n.locale).toFormat('yyyy/LL/dd t');
		return this.$t('orders.management.order_modal.inquiry_request_info', { date: formattedDate });
	}

	/**
	 * Body text of the school order cancellation modal:
	 * the refund description when there is an amount to refund, a simple confirmation otherwise.
	 *
	 * @return {string}
	 */
	private get schoolCancelDescription(): string {
		const refundAmount = (this.order.data.costs?.totalWithTip || 0) + (this.order.data.costs?.credits?.amount || 0);
		return refundAmount > 0
			? this.getSchoolOrderCancelText('cancel')
			: this.$t('orders.management.cancel_school_order_modal.cancel_description_no_refund');
	}

	/**
	 * Generates the text in the warning modal when cancelling or refunding a school order.
	 *
	 * @param {'refund' | 'cancel'} action
	 * @return {string}
	 */
	private getSchoolOrderCancelText(action: 'cancel' | 'refund'): string {
		const { menu_configuration, menu_manager_config } = this.restaurant;

		const totalWithTip = this.order.data.costs?.totalWithTip || 0;
		const creditsUsed = this.order.data.costs?.credits?.amount || 0;
		const refundAmount = totalWithTip + creditsUsed;

		const expiryDate = DateTime
			.fromISO(menu_manager_config?.end_school_year_date || '')
			.setZone(menu_configuration?.zone || 'America/Toronto')
			.toFormat(this.$t('orders.management.cancel_school_order_modal.date_format'));

		return this.$t(`orders.management.cancel_school_order_modal.${action}_description`, {
			amount: this.$n(refundAmount, 'currency'),
			expiryDate
		});
	}

	/**
	 * Open the create order screen pre-filled from this order.
	 *
	 * @return {void}
	 */
	private redirectToRepeatOrder(): void {
		this.$router.push({ path: '/order-management/create', query: { repeatFrom: String(this.mutableOrder.id) } }).catch(() => {});
	}

	/**
	 * Show the warning modal about an invalid K12 configuration (missing or passed end of school year date).
	 *
	 * @return {void}
	 */
	private showK12MisconfiguredWarning(): void {
		this.warningModalProps = {
			show: true,
			title: this.$t('settings.credits.k12_warning_modal.title'),
			text: this.$t('settings.credits.k12_warning_modal.text'),
			confirmButtonText: this.$t('settings.credits.k12_warning_modal.confirm_btn_text'),
			cancelButtonText: this.$t('settings.credits.k12_warning_modal.cancel_btn_text'),
			onConfirm: () => this.$router.push('/settings#business-settings').catch(() => {})
		};
	}

	/**
	 * Display of confirmation/warning modals before refunding a school order:
	 * - Warning modal if invalid K12 configuration
	 * - Confirmation modal if there is an amount to refund
	 *
	 * @return {boolean}
	 */
	private validateSchoolOrderRefund(): boolean {
		if (this.isK12AndMisconfigured && !this.warningModalProps.show) {
			this.showK12MisconfiguredWarning();
			return false;
		}

		// Display a warning modal if there is an amount to refund
		if (!this.warningModalProps.show) {
			const refundAmount = (this.order.data.costs?.totalWithTip || 0) + (this.order.data.costs?.credits?.amount || 0);

			if (refundAmount > 0) {
				this.warningModalProps = {
					show: true,
					title: this.$t('orders.management.cancel_school_order_modal.refund_title'),
					text: this.getSchoolOrderCancelText('refund'),
					confirmButtonText: this.$t('orders.management.cancel_school_order_modal.btn_continue'),
					cancelButtonText: this.$t('shared.cancel'),
					onConfirm: this.refund
				};
				return false;
			}
		}

		return true;
	}

	/**
	 * Cancel changes to items and undo editing mode
	 *
	 * @return {void}
	 */
	private cancelOrderChanges(): void {
		this.mutableOrder = cloneDeep(this.order);
		this.isSuitesCateringEditing = false;
		this.disableSave = true;
	}

	/**
	 * Copy the mutableOrder object and update the item's quantity (decrease by one or 0) before sending the order to the backend
	 * for costs calculation.
	 * On successful request it updates the costs of the mutable order.
	 *
	 * @param {{ index: number; type: 'decrement'|'remove' }}
	 * @return {Promise<void>}
	 */
	private async getCosts({ index, type }: { index: number; type: 'decrement'|'remove' }): Promise<void> {
		let tempOrder: Order = cloneDeep(this.mutableOrder);
		let tempItems: OrderItem[] | undefined;
		let tempCosts: Costs | undefined;

		// Update the item's quantity depending if we just decrease by 1 or remove it
		type === 'decrement' && this.mutableOrder.data.items[index].quantity > 1
			? tempOrder.data.items[index].quantity--
			: tempOrder.data.items[index].quantity = 0;

		// Guard to prevent removing the last item from the order
		if (!tempOrder.data.items.some(item => item.quantity > 0)) {
			Toast.error(this.$t('orders.management.order_modal.remove_all_items_error'));
			return;
		}

		this.loadingItemIndex = index;
		try {
			const { costs, orderItems } = await this.getOrderCosts(tempOrder);
			tempCosts = costs;
			tempItems = orderItems;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.error_calculating_costs', { errorMessage }));
		}
		finally {
			if (tempCosts && tempItems) {
				this.mutableOrder.data.items = decodeItems(tempItems);
				this.mutableOrder.data.costs = tempCosts;
				this.disableSave = false;
			}
			this.loadingItemIndex = -1;
		}
	}

	/**
	 * Save changes to the order.
	 * If an item has been removed, show a confirmation modal.
	 *
	 * @param {boolean} [saveConfirmed] is the save confirmed in the warning modal
	 * @return {void}
	 */
	private async saveChanges(saveConfirmed?: boolean): Promise<void> {
		const filteredItems = this.mutableOrder.data.items.filter(item => item.quantity > 0);
		// If an item has been removed show confirmation modal
		if (!saveConfirmed && filteredItems.length < this.order.data.items.length) {
			this.warningModalProps = {
				show: true,
				title: this.$t('orders.management.save_order_item_removed.title'),
				text: this.$t('orders.management.save_order_item_removed.description'),
				confirmButtonText: this.$t('orders.management.save_order_item_removed.btn_continue'),
				cancelButtonText: this.$t('shared.cancel'),
				onConfirm: () => this.saveChanges(true)
			};
			return;
		}

		this.warningModalProps.show = false;
		this.loading.save = true;
		let errorMessage: string = '';

		try {
			await this.updateOrder({
				orderId: this.order.id,
				payload: {
					data: {
						items: filteredItems,
						costs: this.mutableOrder.data.costs
					}
				}
			});
		}
		catch (error) {
			errorMessage = error;
			Toast.error(this.$t('orders.management.error_updating_order', { errorMessage }));
		}
		finally {
			if (!errorMessage) {
				this.$emit('refresh', { id: this.order.id });
				this.isSuitesCateringEditing = false;
				this.disableSave = true;
			}
			this.loading.save = false;
		}
	}

	/**
	 * Update the status of the order and emit a re-fetch of orders.
	 *
	 * @param {string} type - The type of action (complete, accept...)
	 * @param {string} [note] - Cancellation note
	 * @param {boolean} [isPayOrder] - On preorder completion, do we charge the CC (true) or send an invoice (false)
	 * @return {Promise<void>}
	 */
	private async updateOrderStatus(type: string, note?: string, isPayOrder?: boolean): Promise<void> {
		const status = this.orderStatusMapping[type];
		this.showModal.chargeOrInvoice = false;
		this.showModal.genericCateringComplete = false;
		this.warningModalProps.show = false;
		this.loading[type] = true;
		let errorMessage: string = '';
		try {
			await this.updateOrder({
				orderId: this.order.id,
				payload: {
					status,
					cancellation_note: note,
					payOrder: isPayOrder
				}
			});
			this.$emit('refresh');
			// Refetch the inquiry request so the quotes list reflects the updated order status
			if (this.order.inquiry_form_request) {
				this.fetchInquiryFormRequestById(this.order.inquiry_form_request.id);
			}
		}
		catch (error) {
			errorMessage = error;

			if (errorMessage === 'preauth_failed') {
				this.warningModalProps = {
					show: true,
					title: this.$t('orders.management.pre_auth_failed_modal.title'),
					text: this.$t('orders.management.pre_auth_failed_modal.description'),
					confirmButtonText: undefined,
					cancelButtonText: this.$t('orders.management.pre_auth_failed_modal.close_btn'),
					onConfirm: () => {}
				};
			}
			else {
				Toast.error(this.$t('orders.management.error_updating_order', { errorMessage }));
			}
		}
		finally {
			this.loading[type] = false;
			if (!errorMessage) {
				this.showModal.declineOrder = false;
				if (note) {
					this.mutableOrder.cancellation_note = note;
				}
				this.mutableOrder.status = status;
				if (status === 'completed' && this.mutableOrder.hasPendingModifyRequest) {
					this.mutableOrder.hasPendingModifyRequest = false;
				}
				this.$emit('refresh', { status });
			}
		}
	}

	/**
	 * Request deposit
	 *
	 * @param {number} amount
	 * @return {Promise<void>}
	 */
	private async requestDeposit(amount: number): Promise<void> {
		this.loading.requestDeposit = true;
		try {
			await this.sendDepositRequest({
				orderId: this.mutableOrder.id,
				email: this.email!,
				amount
			});
			this.showModal.requestDeposit = false;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.request_deposit_modal.error_sending_deposit_request', { errorMessage }));
		}
		finally {
			this.loading.requestDeposit = false;
		}
	}

	/**
	 * Refund the order and set the mutable order as refunded.
	 * Show warning modal if necessary.
	 *
	 * @return {Promise<void>}
	 */
	private async refund(): Promise<void> {
		// Validation guard specific to school orders
		if (this.order.type === 'school') {
			if (!this.validateSchoolOrderRefund()) {
				return;
			}
		}
		// Confirmation modal for non-school orders
		else if (!this.warningModalProps.show) {
			this.warningModalProps = {
				show: true,
				title: this.$t('orders.management.refund_modal.title'),
				text: this.$t('orders.management.refund_modal.description'),
				confirmButtonText: this.$t('orders.management.refund_modal.btn_continue'),
				cancelButtonText: this.$t('shared.cancel'),
				onConfirm: this.refund
			};
			return;
		}

		this.warningModalProps.show = false;
		this.loading.refund = true;
		let status: string = this.mutableOrder.status;
		try {
			await this.refundOrder({ orderId: this.order.id });
			this.$emit('refresh');
			status = 'refunded';
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.error_refunding_order', { errorMessage }));
		}
		finally {
			this.loading.refund = false;
			this.mutableOrder.status = status;
		}
	}

	/**
	 * Request a refund and set the mutable order as refund requested.
	 *
	 * @param {string} note - Refund request reason
	 * @return {Promise<void>}
	 */
	private async requestRefund(note: string): Promise<void> {
		this.loading.refundRequest = true;
		try {
			await this.refundOrder({ orderId: this.order.id, cancellationNote: note });
			this.showModal.refundRequest = false;
			this.mutableOrder.cancellation_note = note;
			this.mutableOrder.status = 'refund-requested';
			this.$emit('refresh', { status: 'refund-requested' });
			if (this.order.inquiry_form_request) {
				this.fetchInquiryFormRequestById(this.order.inquiry_form_request.id);
			}
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.error_updating_order', { errorMessage }));
		}
		finally {
			this.loading.refundRequest = false;
		}
	}

	/**
	 * Complete the order for other orders than suites catering and generic-catering.
	 * For suites catering we show a specific modal, same for generic-catering.
	 *
	 * @return {void}
	 */
	private completeOrder(): void {
		if(this.order.type === 'catering') {
			this.showModal.chargeOrInvoice = true;
		}
		else if (this.order.type === 'generic-catering') {
			if (this.mutableOrder.hasPendingModifyRequest) {
				this.warningModalProps = {
					show: true,
					title: this.$t('orders.management.complete_modif_request_warning.title'),
					text: this.$t('orders.management.complete_modif_request_warning.message'),
					confirmButtonText: this.$t('orders.management.complete_modif_request_warning.confirm_btn'),
					cancelButtonText: this.$t('shared.cancel'),
					onConfirm: () => {
						this.warningModalProps.show = false;
						this.showModal.genericCateringComplete = true;
					}
				};
			}
			else {
				this.showModal.genericCateringComplete = true;
			}
		}
		else {
			this.updateOrderStatus('complete');
		}
	}

	private handleDeliveryChange(): void {
		this.showModal.modifySchoolDelivery = false;
		this.$emit('refresh', { id: this.order.id });
	}

	/**
	 * Cancel an order in progress depending of the order type.
	 * Currently only for generic catering and schools with credits.
	 *
	 * @return {Promise<void>}
	 */
	private async cancelInProgressOrder(): Promise<void> {
		// Generic catering order
		if (this.order.type === 'generic-catering') {
			this.cancelCateringOrder();
		}
		// School order
		else if (this.order.type === 'school' && this.isK12Location) {
			if (this.isK12AndMisconfigured) {
				this.showK12MisconfiguredWarning();
				return;
			}
			this.showModal.cancelSchoolOrder = true;
		}
	}

	/**
	 * Cancel a catering order in-progress.
	 * If the order had the possibility to receive deposits, we show a modal to confirm the cancellation.
	 *
	 * @return {void}
	 */
	private cancelCateringOrder(): void {
		if (!this.order.data.checkout?.costCenter && !this.order.data.checkout?.purchaseOrder && !this.warningModalProps.show) {
			this.warningModalProps = {
				show: true,
				title: this.$t('orders.management.cancel_catering_order_modal.title'),
				text: this.$t('orders.management.cancel_catering_order_modal.description'),
				confirmButtonText: this.$t('orders.management.cancel_catering_order_modal.btn_continue'),
				cancelButtonText: this.$t('shared.cancel'),
				onConfirm: this.cancelInProgressOrder
			};
			return;
		}
		this.updateOrderStatus('decline');
	}

	/**
	 * Cancel a school order in-progress with the cancellation reason selected by the operator.
	 *
	 * @param {string} note
	 * @return {Promise<void>}
	 */
	private async cancelSchoolOrder(note: string): Promise<void> {
		this.loading.cancel = true;
		let errorMessage: string = '';
		try {
			const { updatedOrders } = await this.bulkActions({
				action: 'cancel',
				orderIds: [this.order.id],
				email: this.staffEmail,
				cancellation_note: note
			});

			if (!updatedOrders?.length) {
				Toast.error(this.$t('orders.management.bulk_actions.error_message'));
			}
		}
		catch (error) {
			errorMessage = error;
			Toast.error(this.$t('orders.management.error_updating_order', { errorMessage }));
		}
		finally {
			this.loading.cancel = false;
			this.showModal.cancelSchoolOrder = false;
			if (!errorMessage) {
				// For K12 locations with in-progress orders, set to cancelled; otherwise refunded
				const newStatus = this.isK12Location && this.order.status === 'in-progress' ? 'cancelled' : 'refunded';
				this.mutableOrder.cancellation_note = note;
				this.mutableOrder.status = newStatus;
				this.$emit('refresh', { status: newStatus });
			}
		}
	}

	/**
	 * Update the order modification request properties in the mutable order.
	 *
	 * @param {OrderModificationRequest[]} modificationRequests
	 * @return {void}
	 */
	private updateOrderModificationRequests(modificationRequests: OrderModificationRequest[]): void {
		this.mutableOrder.modifyRequestCount = modificationRequests.length;
		this.mutableOrder.hasPendingModifyRequest = modificationRequests.some(request => request.status === 'pending');
		this.$emit('refresh');
	}

	/**
	 * Fetch inquiry request then redirect to inquires page
	 *
	 * @param {number} requestId
	 * @return {Promise<void>}
	 */
	private async fetchInquiryAndRedirect(requestId: number): Promise<void> {
		try {
			await this.fetchInquiryFormRequestById(requestId);
			this.$router.push('/inquiries').catch(() => {});
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.inquiries.error_fetching_inquiry', { errorMessage }));
		}
	}
}
